"use client";

import Link from "next/link";
import { useEffect, useState, type CSSProperties } from "react";
import { CalendarBlank, IdentificationCard, ShieldCheck } from "@phosphor-icons/react";
import { PlazaConnectionError } from "@/features/plaza/components/PlazaConnectionError";
import { miraNumber } from "@/lib/mira-number";
import { AttestShield } from "./AttestShield";
import styles from "./attest.module.css";

const steps = [
  { icon: "ID", title: "身份核验", detail: "实名认证与权利主体核验，确认肖像权归属。" },
  { icon: "✓", title: "告知与同意", detail: "明确用途、范围与期限，取得单独同意。" },
  { icon: "✎", title: "授权签署", detail: "电子授权书记录用途、期限、地域及分成。" },
  { icon: "◇", title: "形象确权", detail: "形成平台确权记录，为后续授权提供凭证。" },
  { icon: "AI", title: "上架与交付", detail: "授权状态随形象展示，生成内容保留标识。" },
  { icon: "↻", title: "监控与管理", detail: "授权记录持续可查，支持状态管理与撤回。" },
] as const;

export type AttestExample = {
  id: string;
  title: string;
  imageUrl?: string;
  publishedDate: string;
};

export function AttestContent({ examples, unavailable = false }: { examples: AttestExample[]; unavailable?: boolean }) {
  const [cardIndex, setCardIndex] = useState(0);
  const activeIndex = examples.length ? cardIndex % examples.length : 0;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (examples.length < 2 || unavailable || hovered || focused) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        setCardIndex((index) => (index + 1) % examples.length);
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [examples.length, unavailable, hovered, focused]);

  return (
    <main className={styles.page}>
      <section className={styles.hero} aria-labelledby="attest-title">
        <div className={`${styles.wrap} ${styles.heroGrid}`}>
          <div className={styles.heroCopy}>
            <div className={styles.kicker}><span>01</span> RIGHTS FIRST</div>
            <h1 id="attest-title"><span>版权安全</span> · 你的肖像<br />我们守护</h1>
            <div className={styles.heroChips} aria-label="流程要点">
              <span>✓ 真实身份核验</span><span>✓ 电子授权签署</span>
              <span>✓ 存证留痕</span><span>✓ 全流程可追溯</span>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <AttestShield />
          </div>
        </div>
      </section>

      <section className={styles.process} id="verification" aria-labelledby="process-title">
        <div className={styles.wrap}>
          <div className={styles.kicker}><span>02</span> VERIFICATION PROCESS</div>
          <h2 id="process-title">确权流程</h2>
          <div className={styles.steps}>
            {steps.map((step, index) => (
              <article className={styles.step} key={step.title}>
                <span className={styles.stepNumber}>{String(index + 1).padStart(2, "0")}</span>
                <span className={styles.stepIcon} aria-hidden="true">{step.icon}</span>
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.proof} aria-labelledby="proof-title">
        <div className={`${styles.wrap} ${styles.proofGrid}`}>
          <div className={styles.proofCopy}>
            <div className={styles.kicker}><span>03</span> AVATAR REGISTRATION CERTIFICATE</div>
            <h2 id="proof-title">形象登记证书</h2>
          </div>
          {unavailable ? <PlazaConnectionError /> : examples.length === 0 ? (
            <p className={styles.notice}>暂无公开形象登记证书</p>
          ) : (
            <div
              className={styles.deck}
              aria-label="形象登记证书"
              onPointerEnter={() => setHovered(true)}
              onPointerLeave={() => setHovered(false)}
              onPointerCancel={() => setHovered(false)}
              onFocusCapture={() => setFocused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
              }}
            >
              {examples.map((person, index) => {
                const depth = (index - activeIndex + examples.length) % examples.length;
                return (
                  <Link
                    className={styles.card}
                    data-depth={depth}
                    key={person.id}
                    href={`/plaza/${encodeURIComponent(person.id)}`}
                    tabIndex={depth < 5 ? 0 : -1}
                    aria-hidden={depth < 5 ? undefined : true}
                    role={depth === 0 ? undefined : "button"}
                    aria-label={depth === 0 ? `查看${person.title}的形象详情` : `切换到${person.title}的形象登记证书`}
                    onClick={event => { if (depth !== 0) { event.preventDefault(); setCardIndex(index); } }}
                    onKeyDown={event => { if (depth !== 0 && event.key === " ") { event.preventDefault(); setCardIndex(index); } }}
                    style={{ "--depth": depth } as CSSProperties}
                  >
                    <span className={styles.photo}>
                      {person.imageUrl && (
                        // Public catalog photos use the same media source as the plaza.
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={person.imageUrl} alt={`${person.title}形象照`} width={178} height={284} loading="lazy" referrerPolicy="no-referrer" />
                      )}
                    </span>
                    <span className={styles.cardBody}>
                      <span className={styles.cardHeading}>
                        <span className={styles.cardLabel}>MIRAFANS · 形象登记证书</span>
                        <span className={styles.cardName}>{person.title}</span>
                      </span>
                      <span className={styles.cardDetails}>
                      <span className={styles.cardRow}><span className={styles.rowIcon}><IdentificationCard size={17} /></span><span><small>形象编号 / MiraID</small><b>{miraNumber(person.id)}</b></span></span>
                      {person.publishedDate && <span className={styles.cardRow}><span className={styles.rowIcon}><CalendarBlank size={17} /></span><span><small>公开日期 / Date</small><b>{person.publishedDate}</b></span></span>}
                      </span>
                      <span className={styles.cardStatus}><ShieldCheck size={15} />平台已审核 · 公开展示</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className={styles.promise} aria-labelledby="promise-title">
        <div className={styles.wrap}>
          <h2 id="promise-title">你的形象，值得被看见和尊重</h2>
          <Link href="/plaza">进入形象广场 →</Link>
        </div>
      </section>
    </main>
  );
}
